import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0TagStatus, Variant } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import { ChevronRight, Download } from '@factorialco/f0-react/icons/app'
import {
  DocumentsSignatureStatusEnum,
  PerformanceProcessAgreementDocumentCardEsignaturesFragment,
  PerformanceProcessAgreementDocumentCardFragment,
  PerformanceProcessAgreementDocumentCardGenerateDocumentDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import DocumentType from 'modules/core/components/DocumentType'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DocumentModal from 'modules/documents/components/DocumentModal'
import { documentSignatureStatusLabel } from 'modules/documents/lib/Documents'

const STATUS_TO_VARIANT: Record<DocumentsSignatureStatusEnum, Variant> = {
  pending: 'warning',
  partially_signed: 'info',
  completed: 'positive',
  declined: 'critical',
  bounced_email: 'critical',
  cancelled: 'neutral',
  error: 'critical',
  expired: 'critical',
}

const getCompletitionLabel = (
  signatureStatus: DocumentsSignatureStatusEnum | null | undefined,
  esignatures: PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
): string | null => {
  if (!signatureStatus) {
    return i18n.t('performance_process.agreements.agreement_list_card.confirmations', {
      count: 2,
      total: 2,
    })
  }

  return i18n.t('performance_process.agreements.agreement_list_card.signatures', {
    count: esignatures.filter((signature) => signature.status === 'signed').length,
    total: esignatures.length === 0 ? 2 : esignatures.length,
  })
}

interface Props {
  agreement: PerformanceProcessAgreementDocumentCardFragment
  esignatures: PerformanceProcessAgreementDocumentCardEsignaturesFragment[]
}

// TODO: COPY - Action plan e-signature
export const DocumentCard = ({ agreement, esignatures }: Props) => {
  const [openPreview, setOpenPreview] = useState(false)

  const [generateDocument, { loading: generateDocumentLoading }] = useMutation(
    PerformanceProcessAgreementDocumentCardGenerateDocumentDocument,
    {
      extractorFn: (data) => data.performanceMutations.generateDocumentPerformanceAgreement,
    }
  )

  const agreementDocument = agreement.agreementDocuments[0]
  const document = agreementDocument?.document
  const signatureStatus = agreementDocument?.signatureStatus

  const generating = agreement.generatingDocument || generateDocumentLoading
  const canGenerate = agreement._policies?.generateDocument?.can ?? false

  const completitionLabel = getCompletitionLabel(signatureStatus, esignatures)

  const detailLabels = compact([
    completitionLabel,
    agreementDocument?.createdAt &&
      `${i18n.t('documents.employee.list.created_at')} ${i18nDateToString(
        stringToDate(agreementDocument.createdAt, 'backend_datetime'),
        'ui'
      )}`,
  ])

  const onGenerateDocument = async () => {
    const result = await generateDocument({ variables: { id: agreement.id } })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: "Generating document. You'll get a notification when it's done. ",
      })
    }
  }

  return (
    <Box
      width="full"
      flexDirection="row"
      paddingY="s12"
      paddingX="s16"
      alignItems="center"
      justifyContent="spaceBetween"
      border={{ all: { width: 's1', style: 'solid', color: 'grey300' } }}
      borderRadius={{ all: 'abs016' }}
      gap="s8"
    >
      <Box flexDirection="row" alignItems="center" gap="s16">
        <DocumentType mimetype="pdf" />

        <Box gap="s4">
          <Text.Normal size="200" weight="medium" color="textPrimary">
            {i18n.t('performance_process.agreements.page.title')}
          </Text.Normal>

          {detailLabels.length > 0 && (
            <Text.Normal size="200" ellipsis>
              {detailLabels.join(' · ')}
            </Text.Normal>
          )}
        </Box>
      </Box>
      <Box flexDirection="row" alignItems="center" gap="s8">
        {!canGenerate && (
          <F0TagStatus
            text={
              generating
                ? i18n.t('performance_process.agreements.agreement.generating')
                : documentSignatureStatusLabel(signatureStatus ?? 'completed')
            }
            variant={generating ? 'info' : STATUS_TO_VARIANT[signatureStatus ?? 'completed']}
          />
        )}

        {agreementDocument?.fileUrl && (
          <Dropdown
            items={[
              {
                label: i18n.t('documents.download'),
                href: buildUrl(agreementDocument.fileUrl),
                icon: Download,
              },
            ]}
          />
        )}

        {canGenerate && !generating && (
          <F0Button
            label={i18n.t('performance_process.agreements.agreement.generate_document')}
            variant="outline"
            onClick={onGenerateDocument}
          />
        )}

        {document && (
          <F0Button
            label={i18n.t('common.view_document')}
            icon={ChevronRight}
            variant="outline"
            onClick={() => setOpenPreview(true)}
            hideLabel
            round
          />
        )}
      </Box>

      {openPreview && document && (
        <DocumentModal
          documentId={document.id.toString()}
          documents={[document]}
          onClose={() => setOpenPreview(false)}
        />
      )}
    </Box>
  )
}
